import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || '';
const imgSrc = (p) => !p ? '' : /^https?:\/\//i.test(p) ? p : `${API_BASE}${p}`;

const CATEGORIES = [
  { value: 'all',            label: 'All Articles' },
  { value: 'interior-design', label: 'Interior Design' },
  { value: 'modular-kitchen', label: 'Modular Kitchen' },
  { value: 'false-ceiling',  label: 'False Ceiling' },
  { value: 'home-lighting',  label: 'Home Lighting' },
  { value: 'tips-tricks',    label: 'Tips & Tricks' },
  { value: 'trends',         label: '2030 Trends' },
  { value: 'knowledge-hub',  label: 'Knowledge Hub' },
];

// ─── BLOG LISTING PAGE ────────────────────────────────────────────────────────
export function Blog() {
  const [posts, setPosts]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [category, setCategory] = useState('all');

  useEffect(() => {
    setLoading(true);
    const params = category !== 'all' ? `?category=${category}` : '';
    api.get(`/blog${params}`)
      .then(r => setPosts(Array.isArray(r.data)?r.data:(r.data?.posts||r.data?.data||[])))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category]);

  const featured = posts.find(p => p.featured);
  const rest     = posts.filter(p => !p.featured || p !== featured);

  return (
    <>
      <Helmet>
        <title>Blog & Knowledge Hub | Wedoo Interiors — Interior Design Tips Tumakuru</title>
        <meta name="description" content="Expert interior design tips, trends, modular kitchen guides and home décor inspiration from Wedoo Interiors — Tumakuru's premium design studio." />
        <meta name="keywords" content="interior design blog tumakuru, wedoo interiors blog, modular kitchen tips, home décor 2030, false ceiling design ideas" />
        <link rel="canonical" href="https://wedoointeriors.com/blog" />
      </Helmet>

      {/* Hero */}
      <div style={{ paddingTop: '6rem', paddingBottom: '4rem', background: 'var(--ink-2)', borderBottom: '1px solid rgba(245,168,0,0.12)' }}>
        <div className="container">
          <span className="tag-label" style={{ marginBottom: '1rem', display: 'flex' }}>Ideas & Inspiration</span>
          <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(2.2rem,5vw,3.5rem)', fontWeight:600, color: 'var(--cream)', marginBottom: '1rem' }}>
            Knowledge Hub &<br /><em style={{ color: 'var(--gold)' }}>Design Blog</em>
          </h1>
          <p style={{ color: 'var(--muted)', fontSize: '0.95rem', maxWidth: '560px', lineHeight: '1.8' }}>
            Expert guides, trend reports and interior design inspiration curated by the Wedoo Interiors team.
          </p>
        </div>
      </div>

      <section className="section-pad">
        <div className="container">
          {/* Category Filter */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '3rem', overflowX: 'auto', paddingBottom: '0.5rem' }}>
            {CATEGORIES.map(c => (
              <button key={c.value} onClick={() => setCategory(c.value)} style={{
                padding: '0.5rem 1.25rem', fontSize: '0.68rem', fontWeight: '500',
                letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer',
                transition: 'all 0.3s', whiteSpace: 'nowrap',
                background: category === c.value ? 'var(--gold)' : 'var(--ink-3)',
                color: category === c.value ? 'var(--ink)' : 'var(--muted)',
                border: `1px solid ${category === c.value ? 'var(--gold)' : 'var(--ink-5)'}`,
              }}>
                {c.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '5rem', color: 'var(--muted)' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '1rem', opacity: 0.4 }}>✦</div>
              Loading articles...
            </div>
          ) : posts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '5rem' }}>
              <div style={{ fontSize: '3rem', marginBottom: '1.5rem' }}>📝</div>
              <h3 style={{ fontFamily: 'var(--font-serif)', color: 'var(--cream)', marginBottom: '0.75rem' }}>No Articles Yet</h3>
              <p style={{ color: 'var(--muted)', fontSize: '0.88rem' }}>Check back soon — great content is on its way!</p>
            </div>
          ) : (
            <>
              {/* Featured post */}
              {featured && category === 'all' && (
                <Link to={`/blog/${featured.slug}`} style={{ display: 'block', marginBottom: '3rem', textDecoration: 'none' }}>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0',
                    background: 'var(--ink-2)', border: '1px solid var(--ink-4)', borderRadius:'var(--radius)',
                    overflow: 'hidden', transition: 'all 0.4s',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(245,168,0,0.3)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--ink-4)'; }}>
                    <div style={{ height: '380px', overflow: 'hidden', background: 'var(--ink-3)' }}>
                      {featured.coverImage
                        ? <img src={imgSrc(featured.coverImage)} alt={featured.title} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.6s' }}
                            onMouseEnter={e => e.target.style.transform = 'scale(1.05)'}
                            onMouseLeave={e => e.target.style.transform = 'scale(1)'} />
                        : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '4rem', opacity: 0.3 }}>✦</div>
                      }
                    </div>
                    <div style={{ padding: '3rem', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                        <span className="badge badge-gold">Featured</span>
                        <span className="badge badge-muted">{featured.category?.replace(/-/g, ' ')}</span>
                        <span className="badge badge-muted">{featured.readTime} min read</span>
                      </div>
                      <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.5rem,3vw,2.2rem)', fontWeight: 400, color: 'var(--cream)', marginBottom: '1rem', lineHeight: 1.2 }}>
                        {featured.title}
                      </h2>
                      <p style={{ color: 'var(--muted)', fontSize: '0.88rem', lineHeight: '1.8', marginBottom: '1.5rem' }}>{featured.excerpt}</p>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ color: 'var(--gold)', fontSize: '0.72rem', letterSpacing: '0.15em', textTransform: 'uppercase' }}>Read Article →</span>
                        <span style={{ color: 'var(--muted)', fontSize: '0.72rem' }}>
                          {featured.publishedAt ? new Date(featured.publishedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              )}

              {/* Post grid */}
              <div className="grid-3" style={{ gap: '1.5rem' }}>
                {(category === 'all' ? rest : posts).map(post => (
                  <Link key={post._id} to={`/blog/${post.slug}`} style={{ textDecoration: 'none' }}>
                    <article className="card" style={{ overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <div style={{ height: '220px', overflow: 'hidden', background: 'var(--ink-3)', flexShrink: 0 }}>
                        {post.coverImage
                          ? <img src={imgSrc(post.coverImage)} alt={post.title} style={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.6s' }}
                              onMouseEnter={e => e.target.style.transform = 'scale(1.06)'}
                              onMouseLeave={e => e.target.style.transform = 'scale(1)'} />
                          : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', opacity: 0.2 }}>✦</div>
                        }
                      </div>
                      <div style={{ padding: '1.75rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                        <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.85rem', flexWrap: 'wrap' }}>
                          <span className="badge badge-gold" style={{ fontSize: '0.6rem' }}>{post.category?.replace(/-/g, ' ')}</span>
                          <span className="badge badge-muted" style={{ fontSize: '0.6rem' }}>{post.readTime} min</span>
                        </div>
                        <h3 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.25rem', fontWeight: 400, color: 'var(--cream)', marginBottom: '0.75rem', lineHeight: 1.3 }}>
                          {post.title}
                        </h3>
                        <p style={{ color: 'var(--muted)', fontSize: '0.82rem', lineHeight: '1.7', marginBottom: '1.25rem', flex: 1 }}>
                          {post.excerpt?.slice(0, 120)}{post.excerpt?.length > 120 ? '...' : ''}
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid var(--ink-5)', paddingTop: '1rem' }}>
                          <span style={{ color: 'var(--gold)', fontSize: '0.68rem', letterSpacing: '0.12em', textTransform: 'uppercase' }}>Read →</span>
                          <span style={{ color: 'var(--muted)', fontSize: '0.68rem' }}>
                            {post.publishedAt ? new Date(post.publishedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : 'Draft'}
                          </span>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}

// ─── BLOG SINGLE POST PAGE ────────────────────────────────────────────────────
export function BlogPost() {
  const { slug }              = useParams();
  const [post, setPost]       = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/slug/${slug}`)
      .then(r => {
        setPost(r.data);
        // Load related
        return api.get(`/blog?category=${r.data.category}`);
      })
      .then(r => { const d=Array.isArray(r.data)?r.data:(r.data?.data||[]); setRelated(d.filter(p=>p.slug!==slug).slice(0,3)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)' }}>
      Loading...
    </div>
  );

  if (!post) return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: '6rem' }}>
      <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📄</div>
      <h2 style={{ fontFamily: 'var(--font-serif)', color: 'var(--cream)' }}>Article Not Found</h2>
      <Link to="/blog" className="btn btn-gold" style={{ marginTop: '2rem' }}>← Back to Blog</Link>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>{post.metaTitle || post.title} | Wedoo Interiors Blog</title>
        <meta name="description" content={post.metaDesc || post.excerpt} />
        {post.metaKeywords && <meta name="keywords" content={post.metaKeywords} />}
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        {post.coverImage && <meta property="og:image" content={imgSrc(post.coverImage)} />}
        <meta property="og:type" content="article" />
        <link rel="canonical" href={`https://wedoointeriors.com/blog/${post.slug}`} />
        {post.publishedAt && <meta property="article:published_time" content={post.publishedAt} />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.excerpt,
          "author": { "@type": "Organization", "name": "Wedoo Interiors" },
          "publisher": { "@type": "Organization", "name": "Wedoo Interiors", "url": "https://wedoointeriors.com" },
          "datePublished": post.publishedAt,
          "dateModified": post.updatedAt,
          "image": post.coverImage ? imgSrc(post.coverImage) : undefined,
          "mainEntityOfPage": `https://wedoointeriors.com/blog/${post.slug}`
        })}</script>
      </Helmet>

      {/* Cover image */}
      {post.coverImage && (
        <div style={{ height: '55vh', minHeight: '360px', overflow: 'hidden', position: 'relative', marginTop: '0' }}>
          <img src={imgSrc(post.coverImage)} alt={post.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(12,12,12,0.9) 0%, rgba(12,12,12,0.2) 60%)' }} />
          <div className="container" style={{ position: 'absolute', bottom: '2.5rem', left: '50%', transform: 'translateX(-50%)', width: '100%' }}>
            <div style={{ maxWidth: '760px' }}>
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <span className="badge badge-gold">{post.category?.replace(/-/g, ' ')}</span>
                <span className="badge badge-muted">{post.readTime} min read</span>
                {post.views > 0 && <span className="badge badge-muted">{post.views} views</span>}
              </div>
              <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(1.8rem,4vw,3rem)', fontWeight: 400, color: 'var(--cream)', lineHeight: 1.15 }}>
                {post.title}
              </h1>
            </div>
          </div>
        </div>
      )}

      <article style={{ paddingTop: post.coverImage ? '3rem' : '8rem', paddingBottom: '5rem' }}>
        <div className="container">
          <div style={{ maxWidth: '760px', margin: '0 auto' }}>
            {!post.coverImage && (
              <>
                <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
                  <span className="badge badge-gold">{post.category?.replace(/-/g, ' ')}</span>
                  <span className="badge badge-muted">{post.readTime} min read</span>
                </div>
                <h1 style={{ fontFamily: 'var(--font-serif)', fontSize: 'clamp(2rem,4vw,3rem)', fontWeight: 400, color: 'var(--cream)', lineHeight: 1.15, marginBottom: '1rem' }}>
                  {post.title}
                </h1>
              </>
            )}

            {/* Meta */}
            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', marginBottom: '2.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid var(--ink-5)', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--font-serif)', color: 'var(--ink)', fontWeight: '600' }}>W</div>
                <div>
                  <div style={{ fontSize: '0.8rem', color: 'var(--cream)', fontWeight: '500' }}>{post.author || 'Wedoo Interiors Team'}</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>Interior Design Expert</div>
                </div>
              </div>
              {post.publishedAt && (
                <span style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>
                  {new Date(post.publishedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              )}
              {post.tags?.length > 0 && (
                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                  {post.tags.map(tag => <span key={tag} className="badge badge-muted" style={{ fontSize: '0.62rem' }}>#{tag}</span>)}
                </div>
              )}
            </div>

            {/* Excerpt */}
            <p style={{ fontSize: '1.1rem', color: 'var(--cream-2)', lineHeight: '1.9', marginBottom: '2rem', fontFamily: 'var(--font-serif)', fontWeight: 300, fontStyle: 'italic' }}>
              {post.excerpt}
            </p>

            {/* Content */}
            <div className="blog-content" dangerouslySetInnerHTML={{ __html: post.content }} />

            {/* Share */}
            <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid var(--ink-5)' }}>
              <p style={{ color: 'var(--muted)', fontSize: '0.75rem', letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '1rem' }}>Share This Article</p>
              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                {[
                  { label: 'WhatsApp', color: '#25D366', url: `https://api.whatsapp.com/send?text=${encodeURIComponent(post.title + ' ' + window.location.href)}` },
                  { label: 'Facebook', color: '#1877F2', url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}` },
                  { label: 'Twitter/X', color: '#000', url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(window.location.href)}` },
                ].map(s => (
                  <a key={s.label} href={s.url} target="_blank" rel="noopener noreferrer" style={{
                    padding: '0.6rem 1.2rem', fontSize: '0.72rem', fontWeight: '500', letterSpacing: '0.1em',
                    background: s.color, color: '#fff', textTransform: 'uppercase', transition: 'opacity 0.3s'
                  }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                    {s.label}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </article>

      {/* Related posts */}
      {related.length > 0 && (
        <section style={{ paddingBottom: '5rem', borderTop: '1px solid var(--ink-4)' }}>
          <div className="container" style={{ paddingTop: '3.5rem' }}>
            <h2 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.8rem', color: 'var(--cream)', marginBottom: '2rem' }}>
              Related <em style={{ color: 'var(--gold)' }}>Articles</em>
            </h2>
            <div className="grid-3" style={{ gap: '1.5rem' }}>
              {related.map(p => (
                <Link key={p._id} to={`/blog/${p.slug}`} style={{ textDecoration: 'none' }} onClick={() => window.scrollTo(0, 0)}>
                  <div className="card" style={{ overflow: 'hidden' }}>
                    <div style={{ height: '180px', overflow: 'hidden', background: 'var(--ink-3)' }}>
                      {p.coverImage ? <img src={imgSrc(p.coverImage)} alt={p.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.2, fontSize: '2rem' }}>✦</div>}
                    </div>
                    <div style={{ padding: '1.25rem' }}>
                      <h4 style={{ fontFamily: 'var(--font-serif)', fontSize: '1.05rem', color: 'var(--cream)', lineHeight: 1.3 }}>{p.title}</h4>
                      <p style={{ color: 'var(--gold)', fontSize: '0.7rem', marginTop: '0.75rem', letterSpacing: '0.1em' }}>Read →</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <style>{`
        .blog-content { color: var(--cream-2); font-size: 0.95rem; line-height: 1.9; }
        .blog-content h2 { font-family: var(--font-serif); font-size: 1.8rem; font-weight: 400; color: var(--cream); margin: 2.5rem 0 1rem; }
        .blog-content h3 { font-family: var(--font-serif); font-size: 1.4rem; font-weight: 400; color: var(--cream); margin: 2rem 0 0.75rem; }
        .blog-content p { margin-bottom: 1.35rem; }
        .blog-content ul, .blog-content ol { margin: 1.25rem 0 1.25rem 1.5rem; }
        .blog-content li { margin-bottom: 0.6rem; }
        .blog-content strong { color: var(--cream); font-weight: 600; }
        .blog-content em { color: var(--gold); }
        .blog-content blockquote { border-left: 3px solid var(--gold); padding: 1rem 1.5rem; margin: 2rem 0; background: var(--ink-2); font-family: var(--font-serif); font-size: 1.1rem; font-style: italic; color: var(--cream-2); }
        .blog-content img { width: 100%; max-width: 100%; height: auto; margin: 2rem 0; }
        .blog-content a { color: var(--gold); text-decoration: underline; }
        .blog-content hr { border: none; border-top: 1px solid var(--ink-5); margin: 2.5rem 0; }
        @media(max-width:768px){ .blog-cover-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </>
  );
}
